import { AnalyticIdentifier, assertNever } from '@common/neo.tax';

import {
  AdpRunLogo,
  AdpWorkforceNowLogo,
  CeridianDayforceLogo,
  CsvLogo,
  GustoLogo,
  JustworksLogo,
  NetsuiteLogo,
  PaychexLogo,
  PaylocityLogo,
  QuickbooksLogo,
  RipplingLogo,
  TrinetLogo,
  WorkdayLogo,
  ZenefitsLogo,
} from '@neo.tax/shared/assets';
import { TICKETING_PROVIDER_NAMES } from '@neo.tax/shared/constants';
import { HELP_CONNECT_PAYROLL_CSV_URL } from '@neo.tax/shared/constants/help-link.constants';
import { AccountingProvider, PayrollProvider } from '@neo.tax/shared/graphql';

import { ProviderLogo } from '../../../provider-logo';
import {
  ProviderInfo,
  AccountingExpenseCsvUploadInstructions,
  PayrollExpenseCsvUploadInstructions,
  RipplingInstructions,
  ConnectProviderSlideProps,
} from '../slides';

const getAccountingProviderSlideProps = (accountingProvider: AccountingProvider): ProviderInfo => {
  switch (accountingProvider) {
    case AccountingProvider.Quickbooks: {
      return {
        fullName: 'QuickBooks Online',
        name: 'QuickBooks Online',
        logo: <QuickbooksLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_QUICKBOOKS,
          cancelConnectionId: AnalyticIdentifier.LINK_QUICKBOOKS_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_QUICKBOOKS_TRY_AGAIN,
        },
      };
    }
    case AccountingProvider.Netsuite: {
      return {
        fullName: 'Oracle NetSuite',
        name: 'NetSuite',
        logo: <NetsuiteLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_NETSUITE,
          cancelConnectionId: AnalyticIdentifier.LINK_NETSUITE_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_NETSUITE_TRY_AGAIN,
        },
      };
    }
    case AccountingProvider.SelfReported: {
      return {
        fullName: 'Accounting CSV Upload',
        name: 'Accounting CSV Upload',
        logo: <CsvLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.UPLOAD_ACCOUNTING_CSV,
          cancelConnectionId: AnalyticIdentifier.UPLOAD_ACCOUNTING_CSV_CANCEL,
          previousStepId: AnalyticIdentifier.UPLOAD_ACCOUNTING_CSV_PREVIOUS,
          uploadInputId: AnalyticIdentifier.UPLOAD_ACCOUNTING_CSV_INPUT,
          closeAfterSuccessButtonId: AnalyticIdentifier.UPLOAD_ACCOUNTING_CSV_CLOSE_AFTER_SUCCESS_BUTTON,
        },
        DownloadCsvInstructionsComponent: () => <AccountingExpenseCsvUploadInstructions />,
      };
    }
    default:
      assertNever(accountingProvider);
  }
};

const getPayrollProviderSlideProps = (payrollProvider: PayrollProvider): ProviderInfo => {
  switch (payrollProvider) {
    case PayrollProvider.AdpRun: {
      return {
        fullName: 'AdpRun',
        name: 'AdpRun',
        logo: <AdpRunLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_ADP_RUN,
          cancelConnectionId: AnalyticIdentifier.LINK_ADP_RUN_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_ADP_RUN_TRY_AGAIN,
        },
      };
    }
    case PayrollProvider.AdpWorkforceNow: {
      return {
        fullName: 'Adp Workforce Now',
        name: 'Adp Workforce Now',
        logo: <AdpWorkforceNowLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_ADP_WORKFORCE,
          cancelConnectionId: AnalyticIdentifier.LINK_ADP_WORKFORCE_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_ADP_WORKFORCE_TRY_AGAIN,
        },
      };
    }
    case PayrollProvider.CeridianDayforce: {
      return {
        fullName: 'Dayforce',
        name: 'Dayforce',
        logo: <CeridianDayforceLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_CERIDIAN_DAYFORCE,
          cancelConnectionId: AnalyticIdentifier.LINK_CERIDIAN_DAYFORCE_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_CERIDIAN_DAYFORCE_TRY_AGAIN,
        },
      };
    }
    case PayrollProvider.Gusto: {
      return {
        fullName: 'Gusto',
        name: 'Gusto',
        logo: <GustoLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_GUSTO,
          cancelConnectionId: AnalyticIdentifier.LINK_GUSTO_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_GUSTO_TRY_AGAIN,
        },
      };
    }
    case PayrollProvider.Justworks: {
      return {
        fullName: 'Justworks',
        name: 'Justworks',
        logo: <JustworksLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_JUSTWORKS,
          cancelConnectionId: AnalyticIdentifier.LINK_JUSTWORKS_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_JUSTWORKS_TRY_AGAIN,
        },
      };
    }
    case PayrollProvider.PaychexFlex: {
      return {
        fullName: 'Paychex Flex',
        name: 'Paychex Flex',
        logo: <PaychexLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_PAYCHEX,
          cancelConnectionId: AnalyticIdentifier.LINK_PAYCHEX_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_PAYCHEX_TRY_AGAIN,
        },
      };
    }
    case PayrollProvider.Paylocity: {
      return {
        fullName: 'PayLocity',
        name: 'Paylocity',
        logo: <PaylocityLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_PAYLOCITY,
          cancelConnectionId: AnalyticIdentifier.LINK_PAYLOCITY_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_PAYLOCITY_TRY_AGAIN,
        },
      };
    }
    case PayrollProvider.Quickbooks: {
      return {
        fullName: 'QuickBooks',
        name: 'QuickBooks',
        logo: <QuickbooksLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_QUICKBOOKS_PAYROLL,
          cancelConnectionId: AnalyticIdentifier.LINK_QUICKBOOKS_PAYROLL_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_QUICKBOOKS_PAYROLL_TRY_AGAIN,
        },
      };
    }
    case PayrollProvider.Rippling: {
      return {
        fullName: 'Rippling',
        name: 'Rippling',
        logo: <RipplingLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_RIPPLING,
          cancelConnectionId: AnalyticIdentifier.LINK_RIPPLING_CANCEL,
          previousStepId: AnalyticIdentifier.LINK_RIPPLING_TRY_AGAIN,
          uploadInputId: AnalyticIdentifier.RIPPLING_UPLOAD_INPUT,
          closeAfterSuccessButtonId: AnalyticIdentifier.RIPPLING_CLOSE_AFTER_SUCCESS_BUTTON,
        },
        DownloadCsvInstructionsComponent: () => (
          <RipplingInstructions
            templateCsvUrl="https://app.rippling.com/reports/create-report-v2"
            instructionsUrl="https://scribehow.com/embed/How_to_export_your_Rippling_Yearly_Payroll_Report_to_a_CSV__MaOpcNjqT0uXUA71fh-_RA"
          />
        ),
      };
    }
    case PayrollProvider.SelfReported: {
      return {
        fullName: 'Payroll CSV Upload',
        helpIconHref: HELP_CONNECT_PAYROLL_CSV_URL,
        name: 'Payroll CSV Upload',
        logo: <CsvLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.UPLOAD_PAYROLL_CSV,
          cancelConnectionId: AnalyticIdentifier.UPLOAD_PAYROLL_CSV_CANCEL,
          previousStepId: AnalyticIdentifier.UPLOAD_PAYROLL_CSV_PREVIOUS,
          uploadInputId: AnalyticIdentifier.UPLOAD_PAYROLL_CSV_INPUT,
          closeAfterSuccessButtonId: AnalyticIdentifier.UPLOAD_PAYROLL_CSV_CLOSE_AFTER_SUCCESS_BUTTON,
        },
        DownloadCsvInstructionsComponent: () => <PayrollExpenseCsvUploadInstructions />,
      };
    }
    case PayrollProvider.Trinet: {
      return {
        fullName: 'TriNet',
        name: 'TriNet',
        logo: <TrinetLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_TRINET,
          cancelConnectionId: AnalyticIdentifier.LINK_TRINET_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_TRINET_TRY_AGAIN,
        },
      };
    }
    case PayrollProvider.Workday: {
      return {
        fullName: 'Workday',
        name: 'Workday',
        logo: <WorkdayLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_WORKDAY,
          cancelConnectionId: AnalyticIdentifier.LINK_WORKDAY_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_WORKDAY_TRY_AGAIN,
        },
      };
    }
    case PayrollProvider.Zenefits: {
      return {
        fullName: 'Zenefits',
        name: 'Zenefits',
        logo: <ZenefitsLogo />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_ZENEFITS,
          cancelConnectionId: AnalyticIdentifier.LINK_ZENEFITS_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_ZENEFITS_TRY_AGAIN,
        },
      };
    }
    default:
      assertNever(payrollProvider);
  }
};

export const getProviderInfo = ({
  providerName,
  providerType,
}: ConnectProviderSlideProps['provider']): ProviderInfo => {
  switch (providerType) {
    case 'accounting':
      return getAccountingProviderSlideProps(providerName);
    case 'payroll':
      return getPayrollProviderSlideProps(providerName);
    case 'ticketing':
      return {
        fullName: TICKETING_PROVIDER_NAMES[providerName],
        name: TICKETING_PROVIDER_NAMES[providerName],
        logo: <ProviderLogo provider={providerName} />,
        analyticIds: {
          connectId: AnalyticIdentifier.LINK_TICKETING,
          cancelConnectionId: AnalyticIdentifier.LINK_TICKETING_CANCEL,
          tryAgainId: AnalyticIdentifier.LINK_TICKETING_TRY_AGAIN,
        },
      };
    default:
      assertNever(providerType);
  }
};
